// Network test page: clock sync + TCP RTT, UDP echo over a WebRTC DataChannel, HTTP RTT and
// download/upload throughput over one IP family, Cloudflare edge RTT. See SPEC.md "nettest.html".
import {
  Lab, getKey, requireKeyOrExplain, loadDevice, selectedPairInfo, detectPlatform,
  submitResult, fmtMs, fmtMbps, fmtPct, carrierPicker, keepScreenOn, pathLabel,
} from '../common.js';
import { familyLabel } from '../netfamily.js';
import {
  CONN_STATES, STAGE_NAMES, errCode, statsList, newStatsLog, recordStats, iceSummary, localCandCounts, failStageOf,
  failStageText,
} from '../netdiag.js';
import { summarize, jitterRfc3550 } from '../stats.js';

const $ = (id) => document.getElementById(id);

const FAMILIES = ['ipv4', 'ipv6'];
const UDP_COUNT = 400;
const UDP_INTERVAL_MS = 50; // 20 msgs/s
const UDP_GRACE_MS = 2000;
// Time for both ends of the echo to open (ICE + DTLS + SCTP on two transports); mobile
// networks can be slow here. A transport that reaches 'failed' ends the wait earlier.
const UDP_OPEN_TIMEOUT_MS = 25_000;
// Selected-pair readings kept in udp.diag.pairs.
const DIAG_MAX_PAIRS = 8;
// getStats() of both transports while the echo opens (Chrome drops failed candidate pairs, so a
// report taken only when giving up loses the ICE checks; see netdiag.js recordStats).
const STATS_SAMPLE_MS = 1000;
const TCP_PINGS = 30;
const TCP_SPACING_MS = 100;
// HTTP RTT to the run's host: one warm-up (DNS, TCP, TLS; not a sample), then HTTP_PINGS.
const HTTP_PINGS = 10;
const HTTP_SPACING_MS = 100;
const HTTP_BUDGET_MS = 10_000;
const WARMUP_TIMEOUT_MS = 8000;
// Download: doubling search from 1 MB until one transfer takes >= 2 s or reaches 10 MB,
// then DOWN_EXTRA more transfers at that size; the result is the median of the 3.
const DOWN_START = 1_000_000;
const DOWN_MAX = 10_000_000;
const DOWN_TARGET_MS = 2000;
const DOWN_EXTRA = 2;
// Upload: one 1 MB warm-up, then UP_SAMPLES x 2 MB (1 MB when the warm-up took >= UP_SLOW_MS);
// the result is the median of those samples.
const UP_WARMUP = 1_000_000;
const UP_SIZE = 2_000_000;
const UP_SAMPLES = 3;
const UP_SLOW_MS = 2000;
// An extra download/upload sample is skipped when its phase would run past this, so a
// slow link does not stretch the run (and interrupted runs are never submitted).
const BW_BUDGET_MS = 30_000;
const CF_DOWN = 'https://speed.cloudflare.com/__down?bytes=0';
const CF_TRACE = 'https://speed.cloudflare.com/cdn-cgi/trace';
// A main-thread gap this long means the page was frozen or suspended.
const FREEZE_GAP_MS = 2000;
const INTERRUPTED_TEXT = 'توقّف القياس لأنّ الشاشة أُطفئت أو غادرت الصفحة. أعد القياس وأبقِ الشاشة مضاءة.';
// mediasoup-client 3.23 no longer exposes device.sctpCapabilities; this is the standard value.
const SCTP_CAPS = { numStreams: { OS: 1024, MIS: 1024 } };

const PHASES = {
  idle: { label: 'جاهز', from: 0, to: 0 },
  sync: { label: 'مزامنة الساعة', from: 0, to: 6 },
  tcp: { label: 'زمن الاستجابة عبر TCP', from: 6, to: 14 },
  udp: { label: 'زمن الاستجابة عبر UDP', from: 14, to: 66 },
  http: { label: 'زمن الاستجابة عبر HTTP', from: 66, to: 70 },
  down: { label: 'سرعة التنزيل', from: 70, to: 85 },
  up: { label: 'سرعة الرفع', from: 85, to: 93 },
  cf: { label: 'أقرب خادم Cloudflare', from: 93, to: 100 },
  done: { label: 'اكتمل القياس', from: 100, to: 100 },
  error: { label: 'توقّف القياس', from: 0, to: 0 },
};

const S = { phase: 'idle', result: null, submitted: false, error: null };
Object.defineProperty(window, '__lab', {
  configurable: false,
  enumerable: true,
  get: () => ({
    phase: S.phase,
    result: S.result ? JSON.parse(JSON.stringify(S.result)) : null,
    submitted: S.submitted,
    error: S.error,
  }),
});

let picker = null;
let running = false;

// ---------- small helpers ----------

const sleep = (ms) => new Promise((r) => setTimeout(r, Math.max(0, ms)));
const r1 = (x) => (Number.isFinite(x) ? Math.round(x * 10) / 10 : undefined);
const r2 = (x) => (Number.isFinite(x) ? Math.round(x * 100) / 100 : undefined);
const shown = (fmt, x) => (x == null || !Number.isFinite(x) ? '—' : fmt(x));

// Drops undefined / null / non-finite numbers so the server validator only sees real values.
function clean(v) {
  if (Array.isArray(v)) return v.map(clean);
  if (v && typeof v === 'object') {
    const out = {};
    for (const [k, x] of Object.entries(v)) {
      if (x === undefined || x === null) continue;
      if (typeof x === 'number' && !Number.isFinite(x)) continue;
      out[k] = clean(x);
    }
    return out;
  }
  return v;
}

function setText(id, text) {
  const el = $(id);
  if (el) el.textContent = text;
}

function codeOf(err) {
  return (err && (err.code || err.message)) || '';
}

// A run is only valid if the page stayed visible and running the whole time: on a locked
// screen or a suspended tab the timers stall and queued echoes come back with inflated RTTs.
function watchInterruptions() {
  const g = {
    hit: false,
    reason: null,
    check() {
      if (g.hit) {
        const e = new Error('interrupted');
        e.code = 'interrupted';
        throw e;
      }
    },
    stop() {},
  };
  const mark = (reason) => {
    if (!g.hit) {
      g.hit = true;
      g.reason = reason;
    }
  };
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') mark('hidden');
  };
  const onFreeze = () => mark('frozen');
  const onPageHide = () => mark('pagehide');
  let last = performance.now();
  const tick = setInterval(() => {
    const now = performance.now();
    if (now - last > FREEZE_GAP_MS) mark('stalled');
    last = now;
  }, 250);
  document.addEventListener('visibilitychange', onVisibility);
  document.addEventListener('freeze', onFreeze);
  window.addEventListener('pagehide', onPageHide);
  if (document.visibilityState === 'hidden') mark('hidden');
  g.stop = () => {
    clearInterval(tick);
    document.removeEventListener('visibilitychange', onVisibility);
    document.removeEventListener('freeze', onFreeze);
    window.removeEventListener('pagehide', onPageHide);
  };
  return g;
}

function show(el, on = true) {
  if (el) el.classList.toggle('hidden', !on);
}

function keepScreenOnSupported() {
  return typeof navigator.wakeLock?.request === 'function';
}

function explainError(err) {
  const code = (err && (err.name === 'UnsupportedError' ? 'unsupported' : err.message)) || '';
  switch (code) {
    case 'interrupted':
      return INTERRUPTED_TEXT;
    case 'unsupported':
      return 'متصفّحك لا يدعم WebRTC. استعمل Chrome أو Safari بإصدار حديث.';
    case 'no_key':
      return 'الرابط ناقص. اطلب رابط التجربة من الإدارة.';
    case 'unauthorized':
      return 'الرابط غير صالح. اطلب رابط التجربة من الإدارة.';
    case 'rate_limited':
      return 'طلبات كثيرة في وقت قصير. انتظر دقيقة ثمّ أعد المحاولة.';
    case 'timeout':
      return 'لم يردّ الخادم في الوقت المناسب. تحقّق من الاتّصال ثمّ أعد المحاولة.';
    case 'http_401':
    case 'http_403':
      return 'الرابط غير صالح. اطلب رابط التجربة من الإدارة.';
    case 'http_429':
      return 'طلبات كثيرة في وقت قصير. انتظر دقيقة ثمّ أعد المحاولة.';
    default:
      return 'انقطع الاتّصال بالخادم. تحقّق من الإنترنت ثمّ أعد المحاولة.';
  }
}

function setPhase(phase, detail = '') {
  S.phase = phase;
  setText('phase-label', PHASES[phase]?.label ?? '');
  setText('phase-detail', detail);
  if (phase === 'done') setProgress(phase, 1);
  else if (phase !== 'error') setProgress(phase, 0);
}

// frac in [0,1] within the current phase
function setProgress(phase, frac) {
  const p = PHASES[phase];
  if (!p) return;
  const pct = Math.max(0, Math.min(100, p.from + (p.to - p.from) * Math.max(0, Math.min(1, frac))));
  const bar = $('bar');
  if (bar) bar.style.width = pct.toFixed(1) + '%';
  $('progress')?.setAttribute('aria-valuenow', String(Math.round(pct)));
}

async function fetchWithTimeout(url, opts, ms) {
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), ms);
  try {
    return await fetch(url, { ...opts, signal: ac.signal });
  } finally {
    clearTimeout(timer);
  }
}

// ---------- the run's IP family ----------

// Each run is given IPv4 or IPv6 at random: the UDP echo asks the server for a worker that
// ranks it first, and the HTTP part goes to the host name that has only that family, so both
// families are measured on the same phones. `?fam=ipv4|ipv6` forces it (tests).
function pickFamily() {
  const forced = new URLSearchParams(location.search).get('fam');
  if (FAMILIES.includes(forced)) return forced;
  const b = new Uint8Array(1);
  crypto.getRandomValues(b);
  return FAMILIES[b[0] & 1];
}

// Single-family host names (LAB_ORIGIN_V4/V6) from /api/health; nulls when not set up.
async function loadFamilyOrigins() {
  const valid = (u) => (typeof u === 'string' && /^https?:\/\/[a-z0-9.-]+(?::\d{1,5})?$/i.test(u) ? u : null);
  try {
    const res = await fetchWithTimeout('/api/health', { cache: 'no-store' }, 5000);
    const o = (await res.json())?.familyOrigins;
    return { ipv4: valid(o?.ipv4), ipv6: valid(o?.ipv6) };
  } catch {
    return { ipv4: null, ipv6: null };
  }
}

// The one family a set of observations agrees on, else null.
function agreed(values) {
  const set = new Set(values);
  return set.size === 1 ? [...set][0] : null;
}

const familyOfVersion = (v) => (String(v) === '4' ? 'ipv4' : String(v) === '6' ? 'ipv6' : null);

// ---------- phase a: clock sync + TCP RTT ----------

// A slow or lost ping is a measurement (counted in `timeouts`), not a reason to abort:
// dropping those runs would leave out exactly the weak networks we need to see.
async function runTcp(lab, guard) {
  const clock = lab.clock;
  setPhase('sync');
  let syncRtts = [];
  try {
    await clock.syncInitial(15);
    syncRtts = (clock.rttSamples || []).filter((x) => Number.isFinite(x));
  } catch (e) {
    if (codeOf(e) === 'closed') return null;
  }
  guard.check();
  setPhase('tcp');
  const rtts = [...syncRtts];
  let timeouts = 0;
  for (let i = 0; i < TCP_PINGS; i++) {
    const t0 = performance.now();
    try {
      await lab.request('time', {}, 5000);
      rtts.push(performance.now() - t0);
    } catch (e) {
      if (codeOf(e) === 'closed') break;
      timeouts++;
    }
    guard.check();
    setProgress('tcp', (i + 1) / TCP_PINGS);
    await sleep(TCP_SPACING_MS - (performance.now() - t0));
  }
  if (!rtts.length) return timeouts ? { n: 0, timeouts } : null;
  const s = summarize(rtts);
  return { n: s.n, p50: r1(s.p50), p95: r1(s.p95), timeouts };
}

// ---------- phase b: UDP echo over DataChannel ----------

// Diagnostics of one echo run (udp.diag, for successful and failed runs alike): a timeline in
// ms since net.open was sent, and at the end the state of every step: both transports'
// connectionState and ICE/DTLS stats (merged with the samples taken while opening, `logs`),
// the data channels' readyState, the selected pairs and the local candidates (counts only,
// never an address). `failedDir`: the transport whose failure ended the open wait. See
// netdiag.js and SPEC phase b.
function newUdpCtx() {
  return {
    t0: performance.now(), stages: {}, step: 'net_open', err: null, tag: null, preferred: null,
    send: null, recv: null, dp: null, dc: null, pairs: [], diag: null, failedDir: null,
    logs: { send: newStatsLog(), recv: newStatsLog() },
  };
}

function mark(ctx, name) {
  if (STAGE_NAMES.includes(name) && !(name in ctx.stages)) ctx.stages[name] = Math.round(performance.now() - ctx.t0);
}

const capital = (s) => s[0].toUpperCase() + s.slice(1);

function watchTransport(ctx, dir, transport) {
  transport.on('connectionstatechange', (state) => {
    if (CONN_STATES.includes(state)) mark(ctx, dir + capital(state));
  });
  transport.on('icegatheringstatechange', (state) => {
    if (state === 'complete') mark(ctx, `${dir}Gathered`);
  });
}

function pushPair(ctx, dir, p) {
  if (ctx.pairs.length >= DIAG_MAX_PAIRS) return;
  ctx.pairs.push({
    dir, protocol: p?.protocol, family: p?.family, localType: p?.localType, remoteType: p?.remoteType, rttMs: r1(p?.rttMs),
  });
}

async function transportStats(t) {
  if (!t || t.closed) return [];
  try {
    return statsList(await t.getStats());
  } catch {
    return [];
  }
}

// Samples getStats() of both transports every STATS_SAMPLE_MS into ctx.logs, from the moment
// they exist until the channel opened or the wait ended (stop()). sample() takes one now (or
// returns the one in flight).
function startStatsSampler(ctx) {
  let inFlight = null;
  const sample = () => {
    if (!inFlight) {
      inFlight = Promise.all(['send', 'recv'].map(async (dir) => {
        const list = await transportStats(ctx[dir]);
        if (list.length) recordStats(ctx.logs[dir], list);
      }))
        .catch(() => {})
        .finally(() => { inFlight = null; });
    }
    return inFlight;
  };
  const timer = setInterval(sample, STATS_SAMPLE_MS);
  return { sample, stop: () => clearInterval(timer) };
}

// Builds ctx.diag once, before the transports are closed. withPairs: read the selected pairs
// now (a failed run has no readings of its own; a failed transport no longer lists its pairs,
// then its last selected pair seen while sampling is used).
async function takeDiag(ctx, { withPairs = false } = {}) {
  if (ctx.diag) return ctx.diag;
  mark(ctx, 'end');
  const [sl, rl] = await Promise.all([transportStats(ctx.send), transportStats(ctx.recv)]);
  if (withPairs) {
    for (const [dir, t] of [['send', ctx.send], ['recv', ctx.recv]]) {
      if (!t || t.closed) continue;
      const p = await selectedPairInfo(t).catch(() => null);
      pushPair(ctx, dir, p?.protocol ? p : (ctx.logs[dir].selected ?? p));
    }
  }
  const side = (dir, t, list) => (t ? { state: t.connectionState, ice: iceSummary(list, ctx.logs[dir]) } : null);
  const send = side('send', ctx.send, sl);
  const recv = side('recv', ctx.recv, rl);
  const dpState = ctx.dp?.readyState ?? null;
  const dcState = ctx.dc?.readyState ?? null;
  const has = Boolean(send || recv);
  ctx.diag = {
    tag: ctx.tag,
    failStage: failStageOf({
      step: ctx.step, send, recv, dpState, dcState, stages: ctx.stages, failedDir: ctx.failedDir,
    }),
    err: ctx.err,
    stages: { ...ctx.stages },
    sendState: send?.state ?? null,
    recvState: recv?.state ?? null,
    dpState,
    dcState,
    ice: has ? { send: send?.ice, recv: recv?.ice } : null,
    pairs: ctx.pairs.slice(0, DIAG_MAX_PAIRS),
    localCands: has
      ? { send: send ? localCandCounts(sl, ctx.logs.send) : null, recv: recv ? localCandCounts(rl, ctx.logs.recv) : null }
      : null,
  };
  return ctx.diag;
}

function wireConnect(lab, transport, ctx, dir) {
  transport.on('connect', ({ dtlsParameters }, callback, errback) => {
    mark(ctx, `${dir}Connect`);
    lab.request('transport.connect', { transportId: transport.id, dtlsParameters })
      .then(() => {
        mark(ctx, `${dir}ConnectOk`);
        callback();
      }, errback);
  });
}

// Which transport ended the open wait: 'send' | 'recv' when its connectionState is failed or
// closed (both: the one whose state changed first), else null.
function failedTransport(ctx, send, recv) {
  const down = [['send', send], ['recv', recv]]
    .filter(([, t]) => t.connectionState === 'failed' || t.connectionState === 'closed')
    .map(([dir]) => dir);
  if (down.length < 2) return down[0] ?? null;
  const at = (dir) => ctx.stages[`${dir}Failed`] ?? ctx.stages[`${dir}Closed`] ?? Infinity;
  return at('recv') < at('send') ? 'recv' : 'send';
}

// Resolves when x (DataProducer/DataConsumer) is open. Rejects with code 'transport_failed'
// and `dir` as soon as broken() names a failed transport, 'channel_closed', or 'open_timeout'
// after ms.
function waitOpen(x, ms, broken, onOpen) {
  const fail = (code, extra) => Object.assign(new Error(code), { code }, extra);
  return new Promise((resolve, reject) => {
    const t0 = performance.now();
    const check = () => {
      if (x.readyState === 'open') {
        onOpen();
        return resolve();
      }
      if (x.closed) return reject(fail('channel_closed'));
      const dir = broken();
      if (dir) return reject(fail('transport_failed', { dir }));
      if (performance.now() - t0 > ms) return reject(fail('open_timeout'));
      setTimeout(check, 25);
    };
    check();
  });
}

function decodeMsg(msg) {
  try {
    const s = typeof msg === 'string' ? msg : new TextDecoder().decode(msg);
    return JSON.parse(s);
  } catch {
    return null;
  }
}

// `ctx` (newUdpCtx) collects the diagnostics; the caller keeps it, so a run that throws still
// has them (ctx.diag, or ctx.step when it failed before the transports existed).
async function runUdp(lab, guard, prefer, ctx) {
  setPhase('udp', 'تجهيز القناة…');
  ctx.t0 = performance.now();
  ctx.step = 'net_open';
  const open = await lab.request('net.open', { sctpCapabilities: SCTP_CAPS, prefer }, 10000);
  mark(ctx, 'netOpen');
  // The server answered: from here a failure is the browser's setup, until produceData.
  ctx.step = 'net_setup';
  // The server's log tag of this WebSocket (random; neither the key nor the address), so the
  // stored row can be matched with the server's net.* log lines.
  if (typeof open?.tag === 'string' && /^[A-Za-z0-9_-]{1,40}$/.test(open.tag)) ctx.tag = open.tag;
  // The family the serving worker ranks first ('ipv4' when the server has no IPv6).
  const preferred = FAMILIES.includes(open?.preferredFamily) ? open.preferredFamily : null;
  ctx.preferred = preferred;

  let send = null;
  let recv = null;
  let sampler = null;
  let echo = null;
  const rtt = new Float64Array(UDP_COUNT).fill(NaN);
  let received = 0;
  let sent = 0;
  let acceptUntil = Infinity;
  // Echoes go out on `send` and come back on `recv`: two ICE sessions, each with its own pair
  // (and pair switches). Both are read at message 40 and at the end; see pathOf below.
  const pairs = [];
  const readPairs = () => Promise.all([['send', send], ['recv', recv]].map(([dir, t]) => selectedPairInfo(t)
    .catch(() => null)
    .then((p) => {
      pushPair(ctx, dir, p);
      return p;
    })))
    .then((ps) => { for (const p of ps) if (p && p.protocol) pairs.push(p); });

  try {
    const device = await loadDevice(open.routerRtpCapabilities);
    // Inside the try: `finally` closes whichever transport was created.
    send = device.createSendTransport(open.send);
    ctx.send = send;
    recv = device.createRecvTransport(open.recv);
    ctx.recv = recv;
    watchTransport(ctx, 'send', send);
    watchTransport(ctx, 'recv', recv);
    wireConnect(lab, send, ctx, 'send');
    wireConnect(lab, recv, ctx, 'recv');
    send.on('producedata', ({ sctpStreamParameters, label, protocol }, callback, errback) => {
      lab.request('net.produceData', { transportId: send.id, sctpStreamParameters, label, protocol })
        .then((d) => {
          echo = d.echo;
          mark(ctx, 'produced');
          callback({ id: d.id });
        }, errback);
    });
    sampler = startStatsSampler(ctx);

    ctx.step = 'produce';
    const dp = await send.produceData({ ordered: false, maxRetransmits: 0, label: 'echo', protocol: 'lab-echo' });
    ctx.dp = dp;
    // ICE started before 'producedata' (setRemoteDescription), and the DataProducer only
    // listens for 'open' once it exists (after the net.produceData round trip): a channel that
    // opened meanwhile is marked now, when first seen open.
    if (dp.readyState === 'open') mark(ctx, 'dpOpen');
    dp.on('open', () => mark(ctx, 'dpOpen'));
    if (!echo) throw new Error('bad_request');
    ctx.step = 'consume';
    const dc = await recv.consumeData({
      id: echo.id,
      dataProducerId: echo.dataProducerId,
      sctpStreamParameters: echo.sctpStreamParameters,
      label: echo.label,
      protocol: echo.protocol,
    });
    ctx.dc = dc;
    mark(ctx, 'consumed');
    dc.on('open', () => mark(ctx, 'dcOpen'));
    dc.on('message', (msg) => {
      const tr = performance.now();
      if (tr > acceptUntil) return;
      const m = decodeMsg(msg);
      const seq = m && m.s;
      if (!Number.isInteger(seq) || seq < 0 || seq >= UDP_COUNT || !Number.isNaN(rtt[seq])) return;
      if (typeof m.t !== 'number') return;
      rtt[seq] = tr - m.t;
      received++;
    });

    ctx.step = 'open';
    const broken = () => failedTransport(ctx, send, recv);
    try {
      await Promise.all([
        waitOpen(dp, UDP_OPEN_TIMEOUT_MS, broken, () => mark(ctx, 'dpOpen')),
        waitOpen(dc, UDP_OPEN_TIMEOUT_MS, broken, () => mark(ctx, 'dcOpen')),
      ]);
    } catch (e) {
      guard.check();
      sampler.stop();
      ctx.err = errCode(e);
      ctx.failedDir = e?.dir === 'send' || e?.dir === 'recv' ? e.dir : null;
      await takeDiag(ctx, { withPairs: true });
      return { failed: true };
    }
    ctx.step = 'done';
    // The ICE checks as they stood at open; the final report is merged at the end.
    await sampler.sample();
    sampler.stop();

    setText('phase-detail', `0 من ${UDP_COUNT}`);
    const start = performance.now();
    for (let i = 0; i < UDP_COUNT; i++) {
      await sleep(start + i * UDP_INTERVAL_MS - performance.now());
      guard.check();
      const t = performance.now();
      try {
        if (dp.readyState === 'open') dp.send(JSON.stringify({ s: i, t }));
      } catch { /* counted as lost */ }
      sent++;
      if (i === 40) readPairs();
      if (i % 10 === 9) {
        setProgress('udp', (0.9 * (i + 1)) / UDP_COUNT);
        setText('phase-detail', `${i + 1} من ${UDP_COUNT}`);
        if (pairs.some((p) => p.protocol === 'tcp')) show($('udp-warning'), true);
      }
    }
    acceptUntil = performance.now() + UDP_GRACE_MS;
    setText('phase-detail', 'انتظار آخر الردود…');
    const graceStart = performance.now();
    while (performance.now() - graceStart < UDP_GRACE_MS) {
      await sleep(200);
      guard.check();
      setProgress('udp', 0.9 + (0.1 * (performance.now() - graceStart)) / UDP_GRACE_MS);
    }
    acceptUntil = -Infinity;
    guard.check();
    await readPairs();
    await takeDiag(ctx);
  } catch (e) {
    // Keep the state of every step before the transports close (not for interrupted runs,
    // which are never submitted).
    if (!ctx.diag && errCode(e) !== 'interrupted') {
      ctx.err = ctx.err ?? errCode(e);
      await takeDiag(ctx, { withPairs: true }).catch(() => {});
    }
    throw e;
  } finally {
    sampler?.stop();
    try { send?.close(); } catch { /* ignore */ }
    try { recv?.close(); } catch { /* ignore */ }
  }

  const got = [];
  for (let i = 0; i < UDP_COUNT; i++) if (!Number.isNaN(rtt[i])) got.push(rtt[i]);
  const s = got.length ? summarize(got) : null;
  // TCP if either direction used ICE-TCP at any reading. The family only when every reading
  // of both transports agrees (a mixed path gets null rather than the send side's family).
  const protocol = pairs.some((p) => p.protocol === 'tcp') ? 'tcp' : pairs.length ? 'udp' : null;
  const family = agreed(pairs.map((p) => p.family).filter(Boolean));
  if (protocol === 'tcp') show($('udp-warning'), true);
  return {
    sent,
    received,
    lossPct: r2(sent ? ((sent - received) / sent) * 100 : 100),
    p50: r1(s?.p50),
    p95: r1(s?.p95),
    max: r1(s?.max),
    jitter: got.length > 1 ? r1(jitterRfc3550(got)) : undefined,
    protocol,
    family,
    preferred,
    diag: ctx.diag,
  };
}

// ---------- phase c: HTTP RTT, download / upload over the run's family ----------

// Where the HTTP part goes. `base` null: this page's own host (key in a header). Otherwise a
// host name with one address family: the key goes in the query and no custom header or body
// type is sent, so the browser needs no CORS preflight (an extra round trip inside the timed
// request). `seen` collects the family the server reports for every request.
function apiFetch(target, path, key, init, timeoutMs) {
  const opts = { ...init, cache: 'no-store' };
  let url = path;
  if (target.base) {
    url = `${target.base}${path}${path.includes('?') ? '&' : '?'}k=${encodeURIComponent(key)}`;
    Object.assign(opts, { mode: 'cors', credentials: 'omit' });
  } else {
    opts.headers = { 'X-Lab-Key': key };
  }
  return fetchWithTimeout(url, opts, timeoutMs);
}

async function ping(target, key, timeoutMs) {
  const t0 = performance.now();
  const res = await apiFetch(target, '/api/ping', key, {}, timeoutMs);
  const ms = performance.now() - t0;
  if (!res.ok) throw new Error('http_' + res.status);
  const j = await res.json().catch(() => null);
  target.seen.push(familyOfVersion(j?.ipVersion));
  return ms;
}

// Warm-up then HTTP_PINGS pings. When the family host cannot be reached at all (e.g. IPv6 on
// an IPv4-only phone), the page's own host is used instead and `fallback` says so.
async function runHttp(key, origins, requested, guard) {
  setPhase('http');
  const want = requested && origins[requested] ? requested : null;
  const target = { base: want ? origins[want] : null, seen: [] };
  const http = { requested: want, fallback: false };
  let reachable = true;
  try {
    await ping(target, key, WARMUP_TIMEOUT_MS);
  } catch (e) {
    if (e?.message === 'http_401') throw e;
    guard.check();
    reachable = String(e?.message).startsWith('http_'); // an HTTP answer: reachable, just refused
    if (!reachable && target.base) {
      target.base = null;
      http.fallback = true;
      try {
        await ping(target, key, WARMUP_TIMEOUT_MS);
        reachable = true;
      } catch (e2) {
        if (e2?.message === 'http_401') throw e2;
      }
    }
  }
  guard.check();
  const rtts = [];
  let timeouts = 0;
  const t0 = performance.now();
  for (let i = 0; reachable && i < HTTP_PINGS && performance.now() - t0 < HTTP_BUDGET_MS; i++) {
    const ti = performance.now();
    try {
      rtts.push(await ping(target, key, 5000));
    } catch (e) {
      if (e?.message === 'http_401') throw e;
      timeouts++;
    }
    guard.check();
    setProgress('http', (i + 1) / HTTP_PINGS);
    await sleep(HTTP_SPACING_MS - (performance.now() - ti));
  }
  if (rtts.length) {
    const s = summarize(rtts);
    Object.assign(http, { n: s.n, p50: r1(s.p50), p95: r1(s.p95) });
  }
  http.timeouts = timeouts;
  return { target, http };
}

const mbpsOf = (t) => (t.bytes * 8) / (t.ms / 1000) / 1e6;
const mb = (bytes) => `${(bytes / 1e6).toFixed(0)} MB`;

// Median (nearest rank: the middle of 3, the lower of 2), max and every sample, all at `bytes`.
function rateResult(samples, bytes) {
  const s = summarize(samples);
  return { mbps: r2(s.p50), max: r2(s.max), samples: samples.map(r2), bytes };
}

async function getBlob(target, key, size) {
  const t0 = performance.now();
  const res = await apiFetch(target, `/api/blob?bytes=${size}`, key, {}, 60000);
  if (!res.ok) throw new Error('http_' + res.status);
  const buf = await res.arrayBuffer();
  const ms = Math.max(1, performance.now() - t0);
  target.seen.push(familyOfVersion(res.headers.get('x-lab-ip-version')));
  return { bytes: buf.byteLength, ms };
}

async function postSink(target, key, body) {
  const t0 = performance.now();
  const res = await apiFetch(target, '/api/sink', key, { method: 'POST', body }, 60000);
  if (!res.ok) throw new Error('http_' + res.status);
  const j = await res.json().catch(() => null);
  const ms = Math.max(1, performance.now() - t0);
  target.seen.push(familyOfVersion(j?.ipVersion));
  return { bytes: body.byteLength, ms };
}

// A failed extra sample ends the phase with the samples so far (a wrong key still aborts the run).
function stopOnExtraFailure(e) {
  if (e?.message === 'http_401') throw e;
}

async function runDown(target, key, guard) {
  setPhase('down');
  const t0 = performance.now();
  const searchSteps = Math.ceil(Math.log2(DOWN_MAX / DOWN_START)) + 1;
  let size = DOWN_START;
  let last = null;
  for (let step = 0; ; step++) {
    setText('phase-detail', mb(size));
    last = await getBlob(target, key, size);
    guard.check();
    setProgress('down', (0.6 * (step + 1)) / searchSteps);
    if (last.ms >= DOWN_TARGET_MS || size >= DOWN_MAX) break;
    size = Math.min(size * 2, DOWN_MAX);
  }
  const samples = [mbpsOf(last)];
  for (let i = 0; i < DOWN_EXTRA; i++) {
    if (performance.now() - t0 + last.ms > BW_BUDGET_MS) break;
    setText('phase-detail', `${mb(size)} · ${samples.length + 1} من ${DOWN_EXTRA + 1}`);
    try {
      last = await getBlob(target, key, size);
    } catch (e) {
      stopOnExtraFailure(e);
      break;
    }
    guard.check();
    samples.push(mbpsOf(last));
    setProgress('down', 0.6 + (0.4 * (i + 1)) / DOWN_EXTRA);
  }
  return rateResult(samples, size);
}

function randomBytes(n) {
  const out = new Uint8Array(n);
  for (let off = 0; off < n; off += 65536) {
    crypto.getRandomValues(out.subarray(off, Math.min(n, off + 65536)));
  }
  return out;
}

async function runUp(target, key, guard) {
  setPhase('up');
  const t0 = performance.now();
  const data = randomBytes(UP_SIZE);
  const small = data.slice(0, UP_WARMUP);
  setText('phase-detail', mb(UP_WARMUP));
  const warm = await postSink(target, key, small);
  guard.check();
  setProgress('up', 1 / (UP_SAMPLES + 1));
  // Slow link (the warm-up took >= 2 s): 1 MB samples, which still take >= 2 s each.
  const size = warm.ms >= UP_SLOW_MS ? UP_WARMUP : UP_SIZE;
  const body = size === UP_SIZE ? data : small;
  const samples = [];
  let last = warm;
  for (let i = 0; i < UP_SAMPLES; i++) {
    const nextMs = (last.ms * size) / last.bytes;
    if (performance.now() - t0 + nextMs > BW_BUDGET_MS) break;
    setText('phase-detail', `${mb(size)} · ${i + 1} من ${UP_SAMPLES}`);
    try {
      last = await postSink(target, key, body);
    } catch (e) {
      stopOnExtraFailure(e);
      break;
    }
    guard.check();
    samples.push(mbpsOf(last));
    setProgress('up', (i + 2) / (UP_SAMPLES + 1));
  }
  // No sample fit the budget (very slow or broken link): report the warm-up.
  if (!samples.length) return rateResult([mbpsOf(warm)], warm.bytes);
  return rateResult(samples, size);
}

// ---------- phase d: Cloudflare edge (non-fatal) ----------

// Time Cloudflare spent on the request, from its Server-Timing header (exposed to us by
// access-control-expose-headers). Our own RTT numbers contain no server work, so this
// is subtracted, as Cloudflare's own speed test does.
function cfServerMs(res) {
  const st = res.headers.get('server-timing') || '';
  const total = /(?:^|,)\s*cfRequestDuration;dur=([\d.]+)/.exec(st);
  if (total) return Number(total[1]) || 0;
  let sum = 0;
  for (const m of st.matchAll(/(?:^|,)\s*cfSpeed\w*;dur=([\d.]+)/g)) sum += Number(m[1]) || 0;
  return sum;
}

// requestStart -> responseStart of this exact URL (needs Timing-Allow-Origin), else null.
function ttfbMs(url) {
  try {
    const list = performance.getEntriesByName(url, 'resource');
    const e = list[list.length - 1];
    if (e && e.requestStart > 0 && e.responseStart >= e.requestStart) return e.responseStart - e.requestStart;
  } catch { /* no resource timing */ }
  return null;
}

async function runCf() {
  setPhase('cf');
  const times = [];
  let headerColo = null;
  const run = Math.random().toString(36).slice(2, 10);
  for (let i = 0; i < 8; i++) {
    const url = `${CF_DOWN}&measId=${run}${i}`;
    const t0 = performance.now();
    try {
      const res = await fetchWithTimeout(url, { cache: 'no-store', credentials: 'omit' }, 5000);
      await res.arrayBuffer();
      const wall = performance.now() - t0;
      await sleep(0); // let the resource timing entry land
      const ttfb = ttfbMs(url);
      const sample = Math.max(0, (ttfb ?? wall) - cfServerMs(res));
      if (i > 0) times.push(sample);
      headerColo = headerColo || res.headers.get('cf-meta-colo');
    } catch { /* skip sample */ }
    setProgress('cf', ((i + 1) / 9));
  }
  let colo = null;
  try {
    const res = await fetchWithTimeout(CF_TRACE, { cache: 'no-store', credentials: 'omit' }, 5000);
    const text = await res.text();
    const m = /^colo=([A-Za-z]{3,4})\s*$/m.exec(text);
    if (m) colo = m[1].toUpperCase();
  } catch { /* ignore */ }
  if (!colo && headerColo && /^[A-Za-z]{3,4}$/.test(headerColo)) colo = headerColo.toUpperCase();
  if (!times.length && !colo) return null;
  return { p50: times.length ? r1(summarize(times).p50) : undefined, colo: colo ?? undefined };
}

// ---------- verdict + rendering ----------

function verdict(udp, wsLost) {
  if (wsLost) return { text: 'انقطع الاتّصال بالخادم أثناء القياس — أعد القياس', cls: 'bad' };
  if (!udp) return { text: 'تعذّر اتّصال WebRTC بالخادم — البثّ لن يعمل على هذه الشبكة', cls: 'bad' };
  if (udp.protocol === 'tcp') return { text: 'الشبكة تمنع UDP — البثّ سيعمل أبطأ', cls: 'warn' };
  const p95 = udp.p95 ?? Infinity;
  const loss = udp.lossPct ?? 100;
  if (p95 <= 150 && loss <= 1) return { text: 'ممتاز للبثّ الفوريّ', cls: 'ok' };
  if (p95 <= 220 && loss <= 3) return { text: 'جيّد للبثّ الفوريّ', cls: 'ok' };
  return { text: 'ضعيف — قد يتقطّع البثّ', cls: 'bad' };
}

// " (تعذّر IPv6)" when the run was given one family and used the other.
function failedNote(given, used) {
  return given && used && given !== used ? ` (تعذّر ${familyLabel(given)})` : '';
}

// "UDP · IPv6", or "عبر TCP · IPv4 لأنّ UDP ممنوع", plus the failed family if any.
function protoLabel(u) {
  const base = u.protocol === 'tcp' ? `عبر ${pathLabel(u.protocol, u.family)} لأنّ UDP ممنوع` : pathLabel(u.protocol, u.family);
  return base + failedNote(u.preferred, u.family);
}

// "IPv6", or "IPv4 (تعذّر IPv6)" after a fallback to the page's host.
function httpPathLabel(h) {
  if (!h) return '—';
  const used = familyLabel(h.family) ?? '—';
  return h.fallback && h.requested ? `${used} (تعذّر ${familyLabel(h.requested)})` : used;
}

// "12.4 Mbps · أعلى 15.0 Mbps" (median of the samples, then the best one).
function rateText(r) {
  if (!r || !Number.isFinite(r.mbps)) return '—';
  const n = Array.isArray(r.samples) ? r.samples.length : 1;
  if (n < 2 || !Number.isFinite(r.max)) return fmtMbps(r.mbps);
  const keep = (s) => s.replace(' ', ' '); // never break "12.4 Mbps" across lines
  return `${keep(fmtMbps(r.mbps))} · أعلى ${keep(fmtMbps(r.max))}`;
}

function render(result, udpFailed, wsLost) {
  const v = verdict(udpFailed ? null : result.udp, wsLost);
  const el = $('verdict');
  el.textContent = v.text;
  el.classList.remove('ok', 'warn', 'bad');
  el.classList.add(v.cls);

  const u = result.udp || {};
  setText('r-udp-p50', shown(fmtMs, u.p50));
  setText('r-udp-p95', shown(fmtMs, u.p95));
  setText('r-udp-max', shown(fmtMs, u.max));
  setText('r-udp-jitter', shown(fmtMs, u.jitter));
  setText('r-udp-loss', result.udp && !udpFailed
    ? `${shown(fmtPct, u.lossPct)} (وصلت ${u.received} من ${u.sent})`
    : '—');
  setText('r-udp-proto', protoLabel(u));
  // The step where the echo channel stopped, in plain words.
  setText('udp-fail', udpFailed ? failStageText(u.diag?.failStage, u.diag) : '');
  show($('udp-fail'), udpFailed && !wsLost);
  setText('r-tcp-p50', shown(fmtMs, result.tcp?.p50));
  const tcpLate = result.tcp?.timeouts ? ` (${result.tcp.timeouts} بلا ردّ)` : '';
  setText('r-tcp-p95', shown(fmtMs, result.tcp?.p95) + tcpLate);
  const httpLate = result.http?.timeouts ? ` (${result.http.timeouts} بلا ردّ)` : '';
  setText('r-http-p50', shown(fmtMs, result.http?.p50) + httpLate);
  setText('r-http-path', httpPathLabel(result.http));
  setText('r-down', rateText(result.down));
  setText('r-up', rateText(result.up));
  setText('r-cf', shown(fmtMs, result.cf?.p50));
  setText('r-colo', result.cf?.colo || '—');
  setText('r-clock', '± ' + shown(fmtMs, result.clockUncertaintyMs));
  show($('result-card'), true);
}

function showError(msg) {
  const el = $('error');
  el.textContent = msg;
  show(el, true);
}

// ---------- main flow ----------

async function run() {
  if (running) return;
  const carrier = picker?.value;
  if (!carrier) {
    showError('اختر شبكتك أوّلاً.');
    return;
  }
  const key = getKey();
  if (!key) {
    requireKeyOrExplain($('gate'));
    show($('gate'), true);
    return;
  }
  if (typeof RTCPeerConnection === 'undefined') {
    showError('متصفّحك لا يدعم WebRTC. استعمل Chrome أو Safari بإصدار حديث.');
    return;
  }

  running = true;
  // Before the first await: the wake lock request belongs to this tap.
  const wake = keepScreenOn();
  const guard = watchInterruptions();
  S.result = null;
  S.submitted = false;
  S.error = null;
  show($('error'), false);
  show($('result-card'), false);
  show($('udp-warning'), false);
  show($('udp-fail'), false);
  show($('progress-card'), true);
  setText('submit-status', '');
  const startBtn = $('start');
  startBtn.disabled = true;
  $('note').disabled = true;

  const note = ($('note').value || '').trim().slice(0, 60);
  const platform = detectPlatform();
  const effectiveType = navigator.connection?.effectiveType;
  const tStart = performance.now();
  const requested = pickFamily();
  const originsP = loadFamilyOrigins();
  let lab = null;

  try {
    lab = await Lab.connect();
    guard.check();
    const tcp = await runTcp(lab, guard);
    const clockUncertaintyMs = lab.clock.uncertaintyMs;
    guard.check();

    // The WebSocket may have dropped during the TCP phase; the UDP phase needs it.
    let wsLost = false;
    if (!lab.isOpen) {
      try { lab.close(); } catch { /* ignore */ }
      try {
        lab = await Lab.connect();
      } catch {
        wsLost = true;
      }
      guard.check();
    }

    let udp = null;
    let udpFailed = false;
    const udpCtx = newUdpCtx();
    if (!wsLost) {
      try {
        const u = await runUdp(lab, guard, requested, udpCtx);
        if (u.failed) udpFailed = true;
        else udp = u;
      } catch (e) {
        if (e && e.name === 'UnsupportedError') throw e;
        const code = codeOf(e);
        if (code === 'interrupted' || code === 'unauthorized' || code === 'rate_limited') throw e;
        if (code === 'closed') wsLost = true;
        else {
          udpFailed = true;
          // Failed at net.open (no reply, or an error reply): runUdp took no diag.
          if (!udpCtx.diag) {
            udpCtx.err = udpCtx.err ?? errCode(e);
            await takeDiag(udpCtx).catch(() => {});
          }
        }
      }
    }
    guard.check();
    // A failed echo still says which family it was given and where it stopped.
    const udpFail = { sent: 0, received: 0, preferred: udpCtx.preferred, diag: udpCtx.diag };

    // HTTP RTT, download and upload all go to the host of the run's family.
    let target = { base: null, seen: [] };
    let http = null;
    try {
      ({ target, http } = await runHttp(key, await originsP, requested, guard));
    } catch (e) { if (e?.message === 'http_401') throw e; }
    guard.check();
    let down = null;
    let up = null;
    try { down = await runDown(target, key, guard); } catch (e) { if (e?.message === 'http_401') throw e; }
    guard.check();
    try { up = await runUp(target, key, guard); } catch (e) { if (e?.message === 'http_401') throw e; }
    guard.check();
    // The family the server saw for every one of those requests (null if they differed).
    if (http) http.family = agreed(target.seen);

    let cf = null;
    try { cf = await runCf(); } catch { cf = null; }
    guard.check();

    const result = clean({
      kind: 'nettest',
      carrier,
      note,
      platform: { os: platform.os, browser: platform.browser },
      effectiveType,
      clockUncertaintyMs: r1(clockUncertaintyMs),
      durationS: r1((performance.now() - tStart) / 1000),
      tcp,
      // No UDP row when the WebSocket itself was lost: that is not a UDP block.
      udp: wsLost ? null : udpFailed ? udpFail : udp,
      http,
      down,
      up,
      cf,
    });
    S.result = result;
    setPhase('done');
    setText('phase-detail', '');
    render(result, udpFailed, wsLost && !udp);

    guard.check();
    setText('submit-status', 'جارٍ إرسال النتيجة…');
    try {
      await submitResult('nettest', result);
      S.submitted = true;
      setText('submit-status', 'أُرسلت النتيجة ✓');
    } catch {
      setText('submit-status', 'تعذّر إرسال النتيجة. أعد القياس لاحقاً.');
    }
  } catch (e) {
    S.error = codeOf(e) || 'error';
    if (S.error === 'interrupted') {
      S.result = null;
      show($('result-card'), false);
    }
    setPhase('error');
    showError(explainError(e));
  } finally {
    guard.stop();
    wake.release();
    try { lab?.clock?.stop?.(); } catch { /* ignore */ }
    try { lab?.close(); } catch { /* ignore */ }
    running = false;
    startBtn.disabled = false;
    $('note').disabled = false;
    startBtn.textContent = 'أعد القياس';
  }
}

function init() {
  const gate = $('gate');
  if (!requireKeyOrExplain(gate)) {
    show(gate, true);
    show($('app'), false);
    return;
  }
  picker = carrierPicker($('carrier'), () => show($('error'), false));
  if (!keepScreenOnSupported()) show($('awake-note'), true);
  const et = navigator.connection?.effectiveType;
  if (et) {
    setText('etype', `نوع الاتّصال حسب المتصفّح: ${et}`);
    show($('etype'), true);
  }
  $('start').addEventListener('click', run);
}

init();
